import { Action } from 'redux';
import { ContractAddendum } from '../../../models/Entities/Contracts/ContractAddendum';
import { ContractNegotiation } from '../../../models/Entities/Contracts/ContractNegotiation';
import { ContractNegotiationConcept } from '../../../models/Entities/Contracts/ContractNegotiationConcept';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractState, ContractUnloadState } from '../../states/ContractState';

const FindContract = (currentState: ContractState, contractId: number): Contract | undefined =>
  currentState.list?.find((item: Contract) => item.id === contractId);

const UpdateContractInList = (currentState: ContractState, updatedContract?: Contract): Contract[] => {
  if (!updatedContract) return currentState.list || [];
  return (currentState.list || []).map((item: Contract) =>
    item.id === updatedContract.id ? updatedContract : item
  );
};

const BuildState = (currentState: ContractState, updatedContract?: Contract): ContractState => ({
  ...currentState,
  list: UpdateContractInList(currentState, updatedContract),
});

const UpsertConcept = (
  concepts: ContractNegotiationConcept[] | undefined,
  concept: ContractNegotiationConcept
): ContractNegotiationConcept[] => {
  const current = concepts || [];
  const exists = current.some((c: ContractNegotiationConcept) => c.id === concept.id);
  if (!exists) return [...current, concept];
  return current.map((c: ContractNegotiationConcept) => (c.id === concept.id ? concept : c));
};

const UpdateNegotiationInContract = (
  contract: Contract,
  negotiationId: string,
  updater: (neg: ContractNegotiation) => ContractNegotiation
): Contract => ({
  ...contract,
  negotiations: (contract.negotiations || []).map((n: ContractNegotiation) =>
    n.id === negotiationId ? updater(n) : n
  ),
  addendums: (contract.addendums || []).map((addendum: ContractAddendum) => ({
    ...addendum,
    negotiations: (addendum.negotiations || []).map((n: ContractNegotiation) =>
      n.id === negotiationId ? updater(n) : n
    ),
  } as ContractAddendum)),
});

const HandleNegotiationUpdate = (
  currentState: ContractState,
  contractId: number,
  negotiationId: string,
  updater: (neg: ContractNegotiation) => ContractNegotiation
): ContractState => {
  const contract = FindContract(currentState, contractId);
  if (!contract) return currentState;
  return BuildState(currentState, UpdateNegotiationInContract(contract, negotiationId, updater));
};

const HandleConceptUpsert = (
  currentState: ContractState,
  contractId: number,
  concept: ContractNegotiationConcept
): ContractState =>
  HandleNegotiationUpdate(currentState, contractId, concept.contractNegotiationId, (neg: ContractNegotiation) => ({
    ...neg,
    concepts: UpsertConcept(neg.concepts, concept),
  }));

const HandleConceptStatus = (
  currentState: ContractState,
  contractId: number,
  concept: ContractNegotiationConcept,
  partial: Partial<ContractNegotiationConcept>
): ContractState =>
  HandleNegotiationUpdate(currentState, contractId, concept.contractNegotiationId, (neg: ContractNegotiation) => ({
    ...neg,
    concepts: (neg.concepts || []).map((c: ContractNegotiationConcept) =>
      c.id === concept.id ? ({ ...c, ...partial } as ContractNegotiationConcept) : c
    ),
  }));

const HandleConceptDelete = (
  currentState: ContractState,
  contractId: number,
  contractNegotiationId: string,
  conceptId: string
): ContractState =>
  HandleNegotiationUpdate(currentState, contractId, contractNegotiationId, (neg: ContractNegotiation) => ({
    ...neg,
    concepts: (neg.concepts || []).filter((c: ContractNegotiationConcept) => c.id !== conceptId),
  }));

const Handlers: Record<string, (currentState: ContractState, action: any) => ContractState> = {
  CONTRACTNEGOTIATIONCONCEPT_GET_ALL_REQUEST_ACTION: (currentState, action) =>
    HandleNegotiationUpdate(currentState, action.contractId, action.contractNegotiationId, (neg) => ({
      ...neg,
      isGettingNegotiationConcepts: true,
      isGettingNegotiationConceptsSuccess: false,
      FailGettingNegotiationConcepts: false,
      ErrorGettingNegotiationConcepts: undefined,
    } as ContractNegotiation)),
  CONTRACTNEGOTIATIONCONCEPT_GET_ALL_RECEIVE_ACTION: (currentState, action) =>
    HandleNegotiationUpdate(currentState, action.contractId, action.contractNegotiationId, (neg) => ({
      ...neg,
      concepts: action.contractNegotiationConcepts,
      isGettingNegotiationConcepts: false,
      isGettingNegotiationConceptsSuccess: true,
      FailGettingNegotiationConcepts: false,
      ErrorGettingNegotiationConcepts: undefined,
    } as ContractNegotiation)),
  CONTRACTNEGOTIATIONCONCEPT_GET_ALL_ERROR_ACTION: (currentState, action) =>
    HandleNegotiationUpdate(currentState, action.contractId, action.contractNegotiationId, (neg) => ({
      ...neg,
      isGettingNegotiationConcepts: false,
      isGettingNegotiationConceptsSuccess: false,
      FailGettingNegotiationConcepts: true,
      ErrorGettingNegotiationConcepts: action.error,
    } as ContractNegotiation)),
  CONTRACTNEGOTIATIONCONCEPT_GET_BY_ID_RECEIVE_ACTION: (currentState, action) =>
    HandleConceptUpsert(currentState, action.contractId, action.contractNegotiationConcept),
  CONTRACTNEGOTIATIONCONCEPT_CREATE_RECEIVE_ACTION: (currentState, action) =>
    HandleConceptUpsert(currentState, action.contractId, action.contractNegotiationConcept),
  CONTRACTNEGOTIATIONCONCEPT_UPDATE_REQUEST_ACTION: (currentState, action) =>
    HandleConceptStatus(currentState, action.contractId, action.contractNegotiationConcept, {
      isUpdating: true,
      isUpdatedSuccessfully: undefined,
      failOnUpdating: false,
      error: undefined,
    }),
  CONTRACTNEGOTIATIONCONCEPT_UPDATE_RECEIVE_ACTION: (currentState, action) =>
    HandleConceptUpsert(currentState, action.contractId, action.contractNegotiationConcept),
  CONTRACTNEGOTIATIONCONCEPT_UPDATE_ERROR_ACTION: (currentState, action) =>
    HandleConceptStatus(currentState, action.contractId, action.contractNegotiationConcept, {
      isUpdating: false,
      isUpdatedSuccessfully: false,
      failOnUpdating: true,
      error: action.error,
    }),
  CONTRACTNEGOTIATIONCONCEPT_DELETE_RECEIVE_ACTION: (currentState, action) =>
    HandleConceptDelete(currentState, action.contractId, action.contractNegotiationId, action.id),
};

export default function ContractNegotiationConceptsReducer(
  state: ContractState | undefined,
  action: Action
): ContractState {
  const currentState = state ?? ContractUnloadState;
  const a: any = action;
  const handler = Handlers[a.type];
  if (!handler) return currentState;
  return handler(currentState, a);
}
